import { useState } from "react";
import { Link } from "react-router";
import { api } from "@/lib/api";
import { useQuery, invalidate } from "@/lib/useQuery";
import type { AccountDetail, Activity, Contact, Invoice } from "@/lib/types";
import { whenShort, money, roleLabel, monthYear, shortDate, CONTACT_ROLES } from "@/lib/format";
import { Button, Card, AiTag, Dot, Modal, Field, toast, Tag, Menu } from "@/components/ui";
import { useMe } from "@/app/MeContext";
import { SystemCard } from "./SystemCard";
import { renderMarkdown } from "@/lib/markdown";

export function OverviewTab({ account: a, refetch, onEmail, onTicket }: { account: AccountDetail; refetch: () => void; onEmail: () => void; onTicket: () => void }) {
  const me = useMe();
  const { data: activity } = useQuery<Activity[]>(`activity:${a.id}`, () => api.get<Activity[]>(`/accounts/${a.id}/activity`), [a.id]);
  const { data: billing } = useQuery<{ invoices: Invoice[]; buildTotal: number; buildPaid: number; licenseMonthly: number | null; nextInvoice: string | null; openBalance: number }>(
    me.permissions.seeInvoices ? `billing:${a.id}` : null, () => api.get(`/billing/accounts/${a.id}/summary`), [a.id]);
  const [contactModal, setContactModal] = useState<Contact | "new" | null>(null);
  const sorted = [...a.systems].sort((x, y) => rank(x.status) - rank(y.status));

  return (
    <div className="two-col">
      <div className="left">
        {sorted.map((s) => <SystemCard key={s.id} system={s} account={a} refetch={refetch} onEmail={onEmail} onTicket={onTicket} />)}
        <Card title="Activity" actions={<span className="small muted">All types</span>}>
          <ol className="timeline" style={{ padding: "8px 16px 12px" }}>
            {(activity ?? []).length === 0 && <li className="muted" style={{ padding: "8px 0" }}>No activity yet.</li>}
            {(activity ?? []).map((ev) => (
              <li key={ev.id}>
                <span className="when">{whenShort(ev.occurredAt)}</span>
                <Dot color={ev.color as "red" | null} />
                <span className="what">{ev.refType === "thread" && ev.refId ? <Link to={`/inbox/${ev.refId}`} style={{ color: "inherit" }}>{ev.title}</Link> : ev.title}</span>
                {ev.ai && <AiTag />}
              </li>
            ))}
          </ol>
        </Card>
      </div>
      <div className="right">
        <Card title="Contacts" actions={<Button variant="ghost" size="sm" onClick={() => setContactModal("new")}>Add</Button>}>
          {a.contacts.length === 0 && <div className="empty">No contacts yet.</div>}
          {a.contacts.map((c) => (
            <div key={c.id} className="list-row hover">
              <div className="row gap-8">
                <span style={{ fontWeight: 500 }}>{c.name}</span>
                {c.isPrimary && <Tag color="accent">Primary</Tag>}
                <span className="ml-auto"><Menu trigger={<button className="btn-link small muted">Edit</button>} items={[{ label: "Edit", onClick: () => setContactModal(c) }, ...(c.isPrimary ? [] : [{ label: "Make primary", onClick: async () => { await api.patch(`/contacts/${c.id}`, { isPrimary: true }); refetch(); } }]), { label: "Remove", danger: true, onClick: async () => { if (confirm(`Remove ${c.name}?`)) { await api.del(`/contacts/${c.id}`); refetch(); } } }]} /></span>
              </div>
              <span className="small muted-2">{[c.title, roleLabel(c.role)].filter(Boolean).join(" · ")}</span>
              <span className="small muted">{[c.email, c.phone].filter(Boolean).join(" · ")}</span>
              <div className="row gap-8 mobile-only mt-4">
                {c.email && <a className="btn sm" href={`mailto:${c.email}`}>Email</a>}
                {c.phone && <a className="btn sm" href={`tel:${c.phone}`}>Call</a>}
              </div>
            </div>
          ))}
        </Card>
        <Card className="pad">
          <h2 style={{ fontSize: 13, marginBottom: 10 }}>Details</h2>
          <dl className="kv">
            <dt>Solution</dt><dd>{a.solutionType || <span className="muted">—</span>}</dd>
            <dt>Location</dt><dd>{[a.city, a.stateCode].filter(Boolean).join(", ") || <span className="muted">—</span>}</dd>
            <dt>ERP</dt><dd>{a.erp || <span className="muted">—</span>}</dd>
            <dt>Partner</dt><dd className={a.partner ? "" : "muted"}>{a.partner ? <Link to={`/partners/${a.partner.id}`}>{a.partner.name}</Link> : "None"}</dd>
            <dt>Domain</dt><dd className={a.domain ? "mono small" : "muted"}>{a.domain || "—"}</dd>
            <dt>Customer since</dt><dd>{a.customerSince ? monthYear(a.customerSince) : <span className="muted">—</span>}</dd>
          </dl>
        </Card>
        {me.permissions.seeInvoices && (
          <Card className="pad">
            <div className="row mb-8"><h2 style={{ fontSize: 13 }}>Billing</h2><span className="ml-auto xs muted">FROM WAVE</span></div>
            {billing ? (
              <dl className="kv">
                <dt>Build</dt><dd className="mono">{billing.buildTotal ? `${money(billing.buildTotal)} · ${billing.buildPaid >= billing.buildTotal ? "paid" : money(billing.buildPaid) + " paid"}` : <span className="muted" style={{ fontFamily: "var(--font)" }}>—</span>}</dd>
                <dt>License</dt><dd className="mono">{billing.licenseMonthly ? `${money(billing.licenseMonthly)}/mo` : <span className="muted" style={{ fontFamily: "var(--font)" }}>—</span>}</dd>
                <dt>Next invoice</dt><dd>{billing.nextInvoice ? shortDate(billing.nextInvoice) : <span className="muted">—</span>}</dd>
                <dt>Open balance</dt><dd className="mono">{money(billing.openBalance, { cents: true })}</dd>
              </dl>
            ) : <span className="muted small">No billing yet.</span>}
          </Card>
        )}
        <Card className="pad col gap-8">
          <h2 style={{ fontSize: 13 }}>Latest note</h2>
          {a.latestNote ? (
            <>
              <div className="prose" dangerouslySetInnerHTML={{ __html: renderMarkdown(a.latestNote.body) }} />
              <span className="small muted">{a.latestNote.authorName} · {shortDate(a.latestNote.createdAt)}</span>
            </>
          ) : <span className="muted small">No notes yet. <Link to={`/accounts/${a.id}/notes`}>Add one</Link>.</span>}
        </Card>
      </div>
      {contactModal && <ContactModal accountId={a.id} contact={contactModal === "new" ? null : contactModal} onClose={() => setContactModal(null)} onSaved={() => { setContactModal(null); refetch(); invalidate("accounts"); }} />}
    </div>
  );
}

function rank(s: string) { return s === "red" ? 0 : s === "yellow" ? 1 : 2; }

export function ContactModal({ accountId, contact, onClose, onSaved }: { accountId: string; contact: Contact | null; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ name: contact?.name ?? "", email: contact?.email ?? "", phone: contact?.phone ?? "", title: contact?.title ?? "", role: contact?.role ?? "", isPrimary: contact?.isPrimary ?? false });
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, role: f.role || null, email: f.email || null, phone: f.phone || null, title: f.title || null };
      if (contact) await api.patch(`/contacts/${contact.id}`, body);
      else await api.post("/contacts", { ...body, accountId });
      onSaved();
    } catch (e) { toast((e as Error).message, true); } finally { setBusy(false); }
  };
  return (
    <Modal title={contact ? "Edit contact" : "Add contact"} onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={busy || !f.name.trim()} onClick={save}>Save</Button></>}>
      <Field label="Name"><input className="input" autoFocus value={f.name} onChange={set("name")} /></Field>
      <div className="form-grid">
        <Field label="Email"><input className="input" type="email" value={f.email} onChange={set("email")} /></Field>
        <Field label="Phone"><input className="input" value={f.phone} onChange={set("phone")} /></Field>
        <Field label="Title"><input className="input" placeholder="Plant manager" value={f.title} onChange={set("title")} /></Field>
        <Field label="Role"><select className="select" value={f.role} onChange={set("role")}><option value="">—</option>{CONTACT_ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}</select></Field>
      </div>
      <label className="row gap-8"><input type="checkbox" checked={f.isPrimary} onChange={(e) => setF((x) => ({ ...x, isPrimary: e.target.checked }))} /> Primary contact</label>
    </Modal>
  );
}
